import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NgxPermissionsService, NgxRolesService } from 'ngx-permissions';
import { Observable, retry, switchMap, throwError, timer } from 'rxjs';
import { tapAllResponses } from 'shared/util-common';

import { GuidelineReviewInfo, RoleDto, UserRole, User, TableOfContentNodeDto } from '@treetop/shared/model';

import { UserHttpService } from './user.http.service';
import { CopyWidgetService } from '../../../../../guideline-page/src/lib/guideline-edit/copy-widget.service';
import { GuidelineSyncInfo } from '../../../../model/src/lib/user.model';
import { PUBLIC_KEY_STORAGE_KEY } from '../../../../util-common/src/lib/services/indexed-db.service';
import { InsightsService } from '../infrastructure/insights.service';
import { MessagesService } from '../infrastructure/messages.service';
import { getRolePermissions } from '../infrastructure/PermissionResolver';
import { ProductFruitsService } from '../infrastructure/product-fruits.service';
import { SmartbookHttpService } from '../infrastructure/smartbook-http.service';

const USER_STORAGE_KEY = 'tt-current-user';
const INIT_RETRY_DELAY_MS = 1000;

// A 5xx on init is usually transient: e.g. Core fails parallel lookups of a user it sees for the first time
const isTransientServerError = (error: unknown): boolean => error instanceof HttpErrorResponse && error.status >= 500;

@Injectable({ providedIn: 'root' })
export class UserService {
  private messages = inject(MessagesService);
  private permissionsService = inject(NgxPermissionsService, { optional: true });
  private roleService = inject(NgxRolesService, { optional: true });
  private router = inject(Router);
  private userHttpService = inject(UserHttpService);
  private messagesService = inject(MessagesService);
  private productFruitsService = inject(ProductFruitsService);
  private smartbookHttpService = inject(SmartbookHttpService);
  private copyWidgetService = inject(CopyWidgetService);

  private insightsService = inject(InsightsService);

  private static readonly INTERACTIVE_LOGIN_PENDING_FLAG = 'tt-interactive-login-pending';

  private trackLoginAfterInit = false;

  public isCurrentUserInClinicalPartnerRole = computed(() => {
    const user = this._user();
    if (!user) {
      return false;
    }

    const roleIds = user.currentRoles?.map((r) => r.id) ?? [];

    return (
      user.currentRoles &&
      (roleIds.includes(UserRole.ClinicalPartnersClinician) ||
        roleIds.includes(UserRole.ClinicalPartnersSeniorClinician) ||
        roleIds.includes(UserRole.ClinicalPartnersEditorClinician) ||
        roleIds.includes(UserRole.ClinicalPartnersOrganisationAdmin))
    );
  });

  public isCurrentUserInSeniorClinicianRole = computed(() => {
    const user = this._user();
    if (!user) {
      return false;
    }

    const roleIds = user.currentRoles?.map((r) => r.id) ?? [];

    return user.currentRoles && roleIds.includes(UserRole.ClinicalPartnersSeniorClinician);
  });

  public organisationsWithEditorPermission = computed(() => {
    const user = this._user();
    if (!user) {
      return [];
    }

    return user.organisations.filter((org) => user.organisationIdsWithEditorPermission.includes(org.id));
  });

  private _user = signal<User | undefined>(undefined);
  public user = this._user.asReadonly();

  public currentUser$: Observable<User> = toObservable(this._user);

  private trackLogin = rxMethod<void>((trigger$) =>
    trigger$.pipe(
      switchMap(() => this.userHttpService.trackLogin()),
      tapAllResponses(
        () => {
          // Login tracked successfully
        },
        (error: HttpErrorResponse) => {
          this.messages.showError(undefined, error);
        },
      ),
    ),
  );

  public setHomeOrganisation = rxMethod<string>((organisationId$) =>
    organisationId$.pipe(
      switchMap((organisationId) => this.userHttpService.setHomeOrganisation(organisationId)),
      tapAllResponses<User, HttpErrorResponse>(
        (user) => {
          this._user.set(user);

          this.messagesService.showInfo('SettingsPage.UserSettingsTab.HomeOrganisationChangedSuccessMessage');
        },
        (error: HttpErrorResponse) => {
          this.messagesService.showError(error.error.Message, error);
        },
      ),
    ),
  );

  public updateCurrentOrganisation = rxMethod<string>((organisationId$) =>
    organisationId$.pipe(
      switchMap((organisationId) => this.userHttpService.setCurrentOrganisation(organisationId)),
      tapAllResponses<User, HttpErrorResponse>(
        () => {
          this.copyWidgetService.clear();

          window.location.reload();

          this.messagesService.showInfo('OrganisationSelector.OrganisationChangedSuccessMessage');
        },
        (error: HttpErrorResponse) => {
          this.messagesService.showError(error.error.Message, error);
        },
      ),
    ),
  );

  public updateRootOrganisation = rxMethod<string>((organisationId$) =>
    organisationId$.pipe(
      switchMap((organisationId) => this.userHttpService.setRootOrganisation(organisationId)),
      tapAllResponses<User, HttpErrorResponse>(
        (user) => {
          this._user.set(user);

          this.copyWidgetService.clear();
          this.messagesService.showInfo('OrganisationSelector.RootOrganisationChangedSuccessMessage');
          window.location.reload();
        },
        (error: HttpErrorResponse) => {
          this.messagesService.showError(error.error.Message, error);
        },
      ),
    ),
  );

  private updatePublicKey = rxMethod<void>((trigger$) =>
    trigger$.pipe(
      switchMap(() => this.smartbookHttpService.getPublicKey()),
      tapAllResponses<string>(
        (publicKey) => {
          localStorage.setItem(PUBLIC_KEY_STORAGE_KEY, publicKey);
        },
        (error) => {
          this.messagesService.showError(undefined, error);
        },
      ),
    ),
  );

  private _initUser = rxMethod<string>((token$) =>
    token$.pipe(
      switchMap((idToken) =>
        this.userHttpService
          .initUser(idToken)
          .pipe(
            retry({ count: 1, delay: (error) => (isTransientServerError(error) ? timer(INIT_RETRY_DELAY_MS) : throwError(() => error)) }),
          ),
      ),
      tapAllResponses<User, Error>(
        (user) => {
          this.setCurrentUser(user);
          this.saveUserToStorage(user);
          this.updatePublicKey();

          // Tracked only once the user is initialised on the server, so it doesn't race init
          if (this.trackLoginAfterInit) {
            this.trackLoginAfterInit = false;
            this.trackLogin();
          }
        },
        (error: Error) => {
          this.messages.showError(undefined, error);
          this.insightsService.trackError(error.message);
          this.router.navigate(['/not-allowed'], { queryParams: { error: error.message } });
        },
      ),
    ),
  );

  public initUser(token?: string) {
    if (token) {
      let shouldTrackLogin = false;
      try {
        if (typeof sessionStorage !== 'undefined') {
          shouldTrackLogin = sessionStorage.getItem(UserService.INTERACTIVE_LOGIN_PENDING_FLAG) === '1';
          if (shouldTrackLogin) {
            sessionStorage.removeItem(UserService.INTERACTIVE_LOGIN_PENDING_FLAG);
          }
        }
      } catch {
        // sessionStorage may be unavailable (e.g. private browsing restrictions)
      }

      if (shouldTrackLogin) {
        this.trackLoginAfterInit = true;
      }

      this._initUser(token);
      return;
    }

    const user = JSON.parse(localStorage.getItem(USER_STORAGE_KEY)) as User;
    if (!user) {
      this.router.navigate(['/not-allowed'], { queryParams: { error: undefined } });
      return;
    }

    this.setCurrentUser(user);
  }

  private initProductFruits(username: string, organisation: string): void {
    this.productFruitsService.init(
      'cKNKAu3SREECMcFZ',
      'de',
      {
        username,
        props: { organisation },
      },
      {
        hideInAppCenterLauncher: true,
      },
    );
  }

  public logoutUser(): Observable<string> {
    localStorage.removeItem(USER_STORAGE_KEY);

    this.copyWidgetService.clear();

    return this.userHttpService.logoutUser();
  }

  public getTableOfContent(): Observable<TableOfContentNodeDto> {
    return this.userHttpService.getTableOfContent();
  }

  public getGuidelinesToReview(): Observable<GuidelineReviewInfo[]> {
    return this.userHttpService.getGuidelinesToReview();
  }

  public getGuidelinesToSync(): Observable<GuidelineSyncInfo[]> {
    return this.userHttpService.getGuidelinesToSync();
  }

  public loadAllUsers(): Observable<User[]> {
    return this.userHttpService.loadAllUsers();
  }

  public loadUsersForGuidelineAuthors(): Observable<User[]> {
    return this.userHttpService.loadUsersForGuidelineAuthors();
  }

  public loadUsersForGuidelineOwners(): Observable<User[]> {
    return this.userHttpService.loadUsersForGuidelineOwners();
  }

  public setCurrentUser(user: User): void {
    const newUser: User = {
      ...user,
      currentOrganisation: user.currentOrganisation || user.currentRootOrganisation,
    };

    this._user.set(newUser);

    this.setPermission(newUser.currentRoles);

    this.redirectByRole(newUser.currentRoles);

    this.insightsService.setAuthenticatedUserContext(newUser.currentOrganisation.id);
    this.initProductFruits(newUser.id, newUser.currentOrganisation.displayName);
  }

  public redirectByRole(roles: RoleDto[] = []): void {
    const roleIds = roles?.map((r) => r.id) ?? [];

    if (roleIds.includes(UserRole.ContentPartnerOrganisationAdmin)) {
      if (window.location.pathname !== '/settings') {
        window.location.href = '/settings';
      }
    }
  }

  private setPermission(roles: RoleDto[]): void {
    if (!roles) {
      return;
    }

    for (const role of roles) {
      const permissions = getRolePermissions(role.id);
      this.roleService?.addRole(role.name, []);
      this.permissionsService?.addPermission(permissions);
    }
  }

  private saveUserToStorage(user: User): void {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  }
}
